// Financial Statistics
export interface FinancialStats {
    label: string;
    value: string;
    change: string;
    icon: any;
}

// School/Unit Information
export interface School {
    id: string;
    name: string;
    income: string;
    expense: string;
    balance: string;
    receivable?: string;
    color: string;
    icon: any;
}

// Transaction
export interface Transaction {
    id: string;
    type: 'income' | 'expense';
    description: string;
    amount: string;
    date: string;
    category: string;
    school?: string;
}

// Budget Item
export interface BudgetItem {
    category: string;
    allocated: string;
    used: string;
    percentage: number;
    color: string;
}

// Chart Data Point
export interface ChartDataPoint {
    month: string;
    income: number;
    expense: number;
}

// Student Data
export interface PaymentHistory {
    id: string;
    date: string;
    amount: string;
    method: string;
    status: 'success' | 'failed' | 'pending';
    receiptNo: string;
    category?: string;
    notes?: string;
    attachments?: string[];
}

export interface Student {
    id: string;
    name: string;
    parentName?: string;
    class: string;
    status: 'active' | 'inactive';
    tuitionStatus: 'paid' | 'pending' | 'overdue';
    totalBill?: string;
    receivable?: string;
    amount?: string;
    paymentHistory?: PaymentHistory[];
    schoolId?: string; // Link to specific school (e.g. 'paud-pekanbaru')
}

// Teacher/Staff Data
export interface Staff {
    id: string;
    nipn?: string;
    name: string;
    position: string;
    department: string;
    status: 'active' | 'inactive';
    schoolId?: string; // Link to specific school (e.g. 'paud-pekanbaru')
}

// Account (Chart of Accounts)
export interface Account {
    id: string;
    code: string;
    name: string;
    category: 'asset' | 'liability' | 'equity' | 'income' | 'expense';
    type: 'asset' | 'liability' | 'equity' | 'income' | 'expense';
    subCategory?: string;
    balanceType: 'debit' | 'credit';
    balance: string; // Read-only
    status: 'active' | 'inactive';
    parentId?: string;
    createdAt?: string;
    updatedAt?: string;
}

// Bank Account
export interface BankAccount {
    id: string;
    bankName: string;
    accountNumber: string;
    accountName: string;
    balance: string;
    type: string;
    linkedAccount: string;
    status: 'active' | 'inactive';
}

// Deposit (Setoran)
export interface Deposit {
    id: string;
    date: string;
    type: 'modal' | 'masuk';
    source: string;
    paymentMethod: string;
    amount: string;
    description: string;
    bankAccountId: string;
    status: 'completed' | 'pending' | 'failed';
}

// Expense (Pengeluaran Biaya)
export interface Expense {
    id: string;
    date: string;
    category: string;
    account: string;
    amount: string;
    method: string;
    status: 'approved' | 'pending' | 'rejected';
}

// Petty Cash Request (Permintaan Kas Laci)
export interface PettyCashRequest {
    id: string;
    date: string;
    applicant: string;
    amount: string;
    purpose: string;
    status: 'approved' | 'pending' | 'rejected';
}

// Capital Deposit (Setoran Modal)
export interface CapitalDeposit {
    id: string;
    date: string;
    source: string;
    targetAccount: string;
    amount: string;
    description: string;
}

// Transfer
export interface Transfer {
    id: string;
    date: string;
    fromUnit: string;
    toUnit: string;
    amount: string;
    description: string;
    status: 'completed' | 'pending' | 'rejected';
}

// Journal Entry
export interface JournalEntry {
    id: string;
    date: string;
    description: string;
    debitAccount: string;
    creditAccount: string;
    amount: string;
    reference?: string;
}

// Ledger Entry
export interface LedgerEntry {
    id: string;
    date: string;
    description: string;
    debit: string;
    credit: string;
    balance: string;
}

// Performa (Performance) Module Data
export interface PerformanceKPI {
    label: string;
    value: string;
    change: string;
    trend: 'up' | 'down';
    icon: any;
}

export interface UnitPerformance {
    kpis: PerformanceKPI[];
    studentTrend: { month: string; total: number; attendance: number; capacity?: number }[];
    insights: string[];
    capacity: number;
}

export interface StaffPerformance extends Staff {
    performanceScore: number;
    attendanceRate: string;
    tasksCompleted: number;
    lastEvaluation: string;
    photo?: string;
}

export interface StudentPerformance extends Student {
    academicScore: number;
    attendanceRate: string;
    behavior: 'Excelent' | 'Good' | 'Fair' | 'Needs Improvement';
}

// Financial Report
export interface FinancialReport {
    period: string;
    generatedDate: string;
    data: any;
}

// Neraca (Balance Sheet) Report Data
export interface NeracaItem {
    name: string;
    amount: string;
}

export interface NeracaData {
    assets: {
        current: NeracaItem[];
        nonCurrent: NeracaItem[];
    };
    liabilities: {
        shortTerm: NeracaItem[];
        longTerm: NeracaItem[];
    };
    equity: {
        base: NeracaItem[];
    };
}

// Notification
export interface Notification {
    id: string;
    title: string;
    message: string;
    time: string;
    type: 'info' | 'success' | 'warning' | 'error';
    read: boolean;
}
